import type { UIRow } from './nodeDefs.js';
import { defineRenderNode, type RenderNodeDefsSubset } from './renderNodeDefs.js';
import { BUNDLED_FONT_OPTIONS, getDefaultBundledFontId } from '../sdf/shapeEngine.js';


const SIZE_RANGE = { min: 1, max: Number.POSITIVE_INFINITY, step: 1, subtype: 'pixels' } as const;
const WIDTH_RANGE = { min: 1, max: Number.POSITIVE_INFINITY, step: 1, subtype: 'pixels' } as const;
const HEIGHT_RANGE = { min: 1, max: Number.POSITIVE_INFINITY, step: 1, subtype: 'pixels' } as const;
const LINE_HEIGHT_RANGE = { min: 0.1, max: 10, step: 0.05 } as const;
const WHITE_SPACE_OPTIONS = [
  { label: 'Pre', value: 'pre' },
  { label: 'Normal', value: 'normal' },
  { label: 'No wrap', value: 'nowrap' },
  { label: 'Pre wrap', value: 'pre-wrap' },
] as const;
const TEXT_HORIZONTAL_ALIGN_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Start', value: 'start' },
  { label: 'End', value: 'end' },
] as const;
const TEXT_VERTICAL_ALIGN_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Start', value: 'start' },
  { label: 'End', value: 'end' },
] as const;
const SHAPE_FIT_OPTIONS = [
  { label: 'Contain', value: 'contain' },
  { label: 'Cover', value: 'cover' },
] as const;
const SHAPE_ALIGN_OPTIONS = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
] as const;

const TEXT_INPUTS = [
  { name: 'size', label: 'Font size', type: 'float', range: SIZE_RANGE },
  { name: 'width', label: 'Width', type: 'float', range: WIDTH_RANGE, optional: true },
  { name: 'height', label: 'Height', type: 'float', range: HEIGHT_RANGE, optional: true },
] as const;

/** The text's own fields, which its selection and caret read too: they are laid out as the text is. */
const TEXT_FIELD_ROWS: UIRow[] = [
  { kind: 'textarea', field: 'text', placeholder: 'Text...', rows: 2 },
  { kind: 'select', field: 'font', label: 'Font', options: BUNDLED_FONT_OPTIONS, default: getDefaultBundledFontId() },
  { kind: 'dragger', field: 'letterSpacing', label: 'Spacing', min: -1, max: 2, step: 0.01, subtype: 'em' },
  { kind: 'section', label: 'Layout' },
  { kind: 'checkbox', field: 'autoWidth', label: 'Auto width', default: true },
  { kind: 'checkbox', field: 'autoHeight', label: 'Auto height', default: true },
  { kind: 'select', field: 'whiteSpace', label: 'White space', options: WHITE_SPACE_OPTIONS, default: 'pre' },
  { kind: 'select', field: 'horizontalAlign', label: 'Horizontal align', options: TEXT_HORIZONTAL_ALIGN_OPTIONS, default: 'center' },
  { kind: 'select', field: 'verticalAlign', label: 'Vertical align', options: TEXT_VERTICAL_ALIGN_OPTIONS, default: 'center' },
  { kind: 'dragger', field: 'lineHeight', label: 'Line height', min: LINE_HEIGHT_RANGE.min, max: LINE_HEIGHT_RANGE.max, step: LINE_HEIGHT_RANGE.step, subtype: 'factor' },
];

const TEXT_PORT_ROWS: UIRow[] = [
  { kind: 'port', ref: 'size' },
  { kind: 'port', ref: 'width' },
  { kind: 'port', ref: 'height' },
];

export const TEXT_NODE_DEFS: RenderNodeDefsSubset<'Shape' | 'Path' | 'Text' | 'TextSelection' | 'TextCaret'> = {
  Shape: defineRenderNode({
    label: 'Shape', cat: 'sdf', subcat: 'glyph',
    inputs: [
      { name: 'width', label: 'Width', type: 'float', range: WIDTH_RANGE },
      { name: 'height', label: 'Height', type: 'float', range: HEIGHT_RANGE },
    ],
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
    ui: [
      { kind: 'shape-asset', field: 'shapeRef', label: 'Asset' },
      { kind: 'port', ref: 'width' },
      { kind: 'port', ref: 'height' },
      { kind: 'select', field: 'fit', label: 'Fit', options: SHAPE_FIT_OPTIONS, default: 'contain' },
      { kind: 'select', field: 'alignX', label: 'Align X', options: SHAPE_ALIGN_OPTIONS, default: 'center' },
      { kind: 'select', field: 'alignY', label: 'Align Y', options: SHAPE_ALIGN_OPTIONS, default: 'center' },
      { kind: 'action', action: 'convert-shape-to-path', label: 'Convert to Path' },
      { kind: 'port', ref: 'shape' },
    ]
  }),

  Path: defineRenderNode({
    label: 'Path', cat: 'sdf', subcat: 'glyph',
    inputs: [],
    outputs: [{ name: 'path', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
    ui: [
      { kind: 'path-summary' },
      { kind: 'select', field: 'fillRule', label: 'Fill rule', options: [
        { label: 'Non-zero', value: 'nonzero' },
        { label: 'Even-odd', value: 'evenodd' },
      ], default: 'nonzero' },
      { kind: 'port', ref: 'path' },
    ],
  }),

  Text: defineRenderNode({
    label: 'Text', cat: 'sdf', subcat: 'glyph',
    inputs: TEXT_INPUTS,
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
    minWidth: 185,
    ui: [...TEXT_PORT_ROWS, ...TEXT_FIELD_ROWS, { kind: 'port', ref: 'shape' }],
  }),

  // A text's marks, laid out as the text is (nodes.rs): they take its inputs
  // and read its fields; `start`..`end` and `at` are bytes of the text.
  TextSelection: defineRenderNode({
    label: 'Text Selection', cat: 'sdf', subcat: 'glyph',
    inputs: TEXT_INPUTS,
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
    minWidth: 185,
    ui: [
      ...TEXT_PORT_ROWS,
      { kind: 'dragger', field: 'start', label: 'Start', min: 0, max: Number.POSITIVE_INFINITY, step: 1 },
      { kind: 'dragger', field: 'end', label: 'End', min: 0, max: Number.POSITIVE_INFINITY, step: 1 },
      ...TEXT_FIELD_ROWS,
      { kind: 'port', ref: 'shape' },
    ],
  }),
  TextCaret: defineRenderNode({
    label: 'Text Caret', cat: 'sdf', subcat: 'glyph',
    inputs: TEXT_INPUTS,
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
    minWidth: 185,
    ui: [
      ...TEXT_PORT_ROWS,
      { kind: 'dragger', field: 'at', label: 'At', min: 0, max: Number.POSITIVE_INFINITY, step: 1 },
      { kind: 'dragger', field: 'caretWidth', label: 'Caret width', min: 0, max: 16, step: 0.1, subtype: 'pixels' },
      ...TEXT_FIELD_ROWS,
      { kind: 'port', ref: 'shape' },
    ],
  }),
};
